// src/api/useApi.ts
import { useCallback } from "react";
import { API_URL } from "../config/config";
import { useAuth } from "../context/AuthContext";

export type RequestFn = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

export function useApi() {
  const { token, login, logout, user } = useAuth();

  const refresh = useCallback(async (): Promise<string | null> => {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include", // sends the refresh cookie
    });
    if (!res.ok) return null;

    const auth = await res.json(); // expects { accessToken, expiresAt }
    if (!auth?.accessToken) return null;

    if (user) login(auth.accessToken, user); // update stored token
    return auth.accessToken as string;
  }, [login, user]);

  const request = useCallback(async (input: RequestInfo, init: RequestInit = {}) => {
    // <-- THIS is where withToken is defined
    const withToken = (t: string | null): RequestInit => {
      const headers: HeadersInit = {
        ...(init.headers || {}),
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      };
      return { ...init, credentials: "include", headers };
    };

    // First attempt with current token
    let res = await fetch(input as string, withToken(token));
    if (res.status !== 401) return res;

    // If unauthorized, try refreshing once
    const newToken = await refresh();
    if (!newToken) {
      logout();
      throw new Error("Unauthorized");
    }

    // Retry with the new token
    res = await fetch(input as string, withToken(newToken));
    return res;
  }, [logout, refresh, token]);

  return { request };
}
